import { ArrowUpRight, Heart, Sparkles, Users, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useWedding } from "@/hooks/useWedding";
import { daysUntil, formatDate } from "@/lib/utils";
import { WEDDING_STAGE_LABELS } from "@/types/wedding";

export function DashboardPage() {
  const { user } = useAuth();
  const { activeOrganization } = useOrganization();
  const { weddings } = useWedding();

  const firstName = user?.fullName.split(" ")[0] ?? "there";
  const upcoming = [...weddings].sort(
    (a, b) => new Date(a.weddingDate).getTime() - new Date(b.weddingDate).getTime()
  );
  const totalGuests = weddings.reduce((sum, w) => sum + (w.guestCountEstimate ?? 0), 0);
  const totalBudget = weddings.reduce((sum, w) => sum + (w.budgetTotal ?? 0), 0);

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow={activeOrganization?.name}
        title={`Good to see you, ${firstName}`}
        description="Here's where every wedding in this organization stands today."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active weddings</CardTitle>
            <Heart className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent className="pt-0">
            <p className="font-display text-3xl font-semibold">{weddings.length}</p>
            <p className="mt-1 text-xs text-muted-foreground">across {activeOrganization?.name ?? "your organization"}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Estimated guests</CardTitle>
            <Users className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent className="pt-0">
            <p className="font-display text-3xl font-semibold">{totalGuests.toLocaleString()}</p>
            <p className="mt-1 text-xs text-muted-foreground">combined across all weddings</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Combined budget</CardTitle>
            <Wallet className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent className="pt-0">
            <p className="font-display text-3xl font-semibold">
              {totalBudget.toLocaleString("en-US", { style: "currency", currency: "MXN", maximumFractionDigits: 0 })}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">tracked across booked weddings</p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Upcoming weddings</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1 pt-0">
            {upcoming.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No weddings yet in this organization.
              </p>
            )}
            {upcoming.map((wedding) => {
              const remaining = daysUntil(wedding.weddingDate);
              return (
                <Link
                  key={wedding.id}
                  to={`/weddings/${wedding.id}`}
                  className="flex items-center justify-between gap-4 rounded-lg px-3 py-3 transition-colors hover:bg-muted"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium">{wedding.coupleNames}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {formatDate(wedding.weddingDate)}
                      {wedding.venueCity ? ` · ${wedding.venueCity}` : ""}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <Badge variant="outline">{WEDDING_STAGE_LABELS[wedding.stage]}</Badge>
                    <span className="w-20 text-right text-xs font-medium text-muted-foreground">
                      {remaining >= 0 ? `${remaining}d left` : "Past"}
                    </span>
                  </div>
                </Link>
              );
            })}
          </CardContent>
        </Card>

        <Card className="flex flex-col justify-between">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-accent" />
              Get set up
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col gap-3 pt-0">
            <p className="text-sm text-muted-foreground">
              This is the platform foundation. Guests, budget, timeline, vendors, and every other module
              connect here as they ship.
            </p>
            <Button variant="outline" className="justify-between" asChild>
              <a href="/settings">
                Review organization settings
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
